<template>
  <div class="item-notifications">
    <component
      :is="notificationMap?.icon"
      :style="{
        width: '2rem',
        height: '2rem',
        color: 'var(--text-color)',
      }"
    />
    <div
      class="flex-column-union"
    >
      <div style="color: var(--color-text-secondary);">
        {{ dayjs(props.notification.createdAtUtc).fromNow(false) }}, {{ formatDate(props.notification.createdAtUtc) }}
      </div>
      <div>
        {{ i18n.global.t(`${translationKeyPrefix}.label`, notificationContext) }}
      </div>
      <div class="item-notifications__description">
        {{ i18n.global.t(`${translationKeyPrefix}.description`, notificationContext) }}
      </div>
    </div>
    <div>
      <QmulusButton
        v-if="!markedAsRead"
        :label="i18n.global.t(`${I18N_PREFIX}.markAsRead`)"
        :loading="isLoading"
        icon-only
        is-inline-action
        severity="primary"
        icon="pi pi-circle-fill"
        @click="markNotificationAsRead"
      />
      <router-link
        v-if="notificationMap?.navigationTarget"
        :to="(notificationMap.navigationTarget as any)"
      >
        <QmulusButton
          :label="i18n.global.t(`${I18N_PREFIX}.goToEvent`)"
          icon-only
          is-inline-action
          severity="primary"
          icon="pi pi-arrow-right"
          @click="emit('close')"
        />
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import QmulusButton from '@qmulus/ui/components/button/qmulus-button.vue';
import {
  FunctionalComponent,
  SVGAttributes,
  computed,
  shallowRef,
} from 'vue';

import UpgradeIcon from '@/assets/icon/upgrade.svg?component';
import {
  ECustomRangeType,
  TRange,
} from '@/components/date-range-picker/date-range-picker.ts';
import {
  EEventType,
} from '@/components/item-notification/item-notification.ts';
import {
  RANGE_QUERY_KEY,
} from '@/composition/use-query-date-range';
import {
  TGetInAppNotificationsResponse,
  markRead,
} from '@/generated/qmulus/axios/core.ts';
import {
  ERouteName,
} from '@/router/route-name';
import {
  useNotificationStore,
} from '@/store/pinia/notification.ts';
import {
  EAppId,
} from '@/types/app-type';
import {
  getAppIcon,
} from '@/types/app-util.ts';
import {
  EHttpStatusCode,
} from '@/types/http.ts';
import {
  formatDate,
} from '@/utils/date-formats.ts';

const I18N_PREFIX = 'components/item-notification/item-notification';

const notificationStore = useNotificationStore();

const props = defineProps<{
  notification: TGetInAppNotificationsResponse['items'][number],
}>();

const emit = defineEmits<{
  (e: 'close'): void,
}>();

// Detection and stop of a lag share one event, whose context always reflects its latest
// state. A notification created once the lag was resolved is the one about the stop.
const isTimeseriesLagStoppedNotification = computed(() => {
  if (props.notification.eventType !== EEventType.TIMESERIES_LAG_DETECTED) {
    return false;
  }
  const resolvedAtUtc = props.notification.context.resolvedAtUtc;
  return resolvedAtUtc !== null && !dayjs(props.notification.createdAtUtc).isBefore(resolvedAtUtc);
});

const translationKeyPrefix = computed(() => {
  if (isTimeseriesLagStoppedNotification.value) {
    return `${I18N_PREFIX}.timeseriesLagStopped`;
  }
  return `${I18N_PREFIX}.event.${props.notification.eventType}`;
});

const notificationContext = computed(() => {
  const context = props.notification.context as any;
  if (
    props.notification.eventType === EEventType.JOB_STARTED
    || props.notification.eventType === EEventType.JOB_FINISHED
  ) {
    context.startedAtUtc = formatDate(context.startedAtUtc);
    context.finishedAtUtc = context.finishedAtUtc ? formatDate(context.finishedAtUtc) : context.finishedAtUtc;
  } else if (props.notification.eventType === EEventType.ALARM_OCCURRED) {
    context.severityTranslation = i18n.global.t(`api.AlarmEventSeverity.${context.severity}`);
  } else if (props.notification.eventType === EEventType.MAINTENANCE_OCCURRENCE_STATE_CHANGED) {
    context.maintenanceStateTranslation = i18n.global.t(`api.MaintenanceStateType.${context.newState}`);
  }

  return context;
});

type TNotificationMap = {
  icon: FunctionalComponent<SVGAttributes, any, any>,
  navigationTarget: {
    name: ERouteName,
    params?: {
      jobId: number,
    } | {
      assetId: number,
    },
    query?: {
      range: string,
    },
  },
};
const notificationMap = computed<TNotificationMap>(() => {
  switch (props.notification.eventType) {
    case EEventType.JOB_STARTED:
    case EEventType.JOB_FINISHED:
      return {
        icon: getAppIcon(EAppId.PRODUCTION_LOGS),
        navigationTarget: {
          name: ERouteName.PRODUCTION_LOGS_JOB_DETAILS,
          params: {
            jobId: props.notification.context.jobId,
          },
        },
      };
    case EEventType.TIMESERIES_LAG_DETECTED:
      return {
        icon: getAppIcon(EAppId.LIVE),
        navigationTarget: {
          name: ERouteName.LIVE_APP_DETAILS,
          params: {
            assetId: props.notification.context.assetId,
          },
        },
      };
    case EEventType.ALARM_OCCURRED: {
      const rangeMap: TRange = {
        rangeType: ECustomRangeType.CUSTOM,
        from: dayjs(props.notification.createdAtUtc).subtract(30, 'minute').toDate(),
        to: dayjs(props.notification.createdAtUtc).add(30, 'minute').toDate(),
      };
      return {
        icon: getAppIcon(EAppId.LIVE),
        navigationTarget: {
          name: ERouteName.LIVE_APP_DETAILS,
          params: {
            assetId: props.notification.context.assetId,
          },
          query: {
            [RANGE_QUERY_KEY]: JSON.stringify(rangeMap),
          },
        },
      }; }
    case EEventType.NEW_QMULUS_RELEASE:
      return {
        icon: UpgradeIcon,
        navigationTarget: {
          name: ERouteName.RELEASE_HIGHLIGHTS,
        },
      };
    case EEventType.MAINTENANCE_OCCURRENCE_STATE_CHANGED:
      return {
        icon: getAppIcon(EAppId.OPERATION_CALENDAR),
        navigationTarget: {
          name: ERouteName.OPERATION_CALENDAR,
        },
      };
    default:
      throw new Error('Unspecified notification');
  }
});

const markedAsRead = shallowRef(!!props.notification.seenAtUtc);

const isLoading = shallowRef(false);
function markNotificationAsRead() {
  isLoading.value = true;
  markRead({
    bodyParams: {
      notificationIds: [
        props.notification.notificationId,
      ],
    },
  })
    .then((response) => {
      markedAsRead.value = response.status === EHttpStatusCode.NO_CONTENT_204;
      notificationStore.loadNotificationUnreadCount(true);
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>

<style lang="scss" scoped>
.item-notifications {
  padding: 0.75rem;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--gap-section);
  border-bottom: solid 1px var(--border-color-subtle);

  &__description {
    color: var(--text-color-secondary);
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    white-space: pre-line;
  }
}
</style>
